import React, { createContext, useContext, useState, useCallback } from 'react';

interface OobSession {
  sessionToken: string;
  eventGuid: string;
  participantGuid: string;
  sessionExpiresAt: Date;
}

interface OobSessionContextType {
  session: OobSession | null;
  setSession: (session: OobSession | null) => void;
  clearSession: () => void;
  isSessionActive: () => boolean;
}

const OobSessionContext = createContext<OobSessionContextType | undefined>(undefined);

export const OobSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<OobSession | null>(() => {
    // Restore from localStorage if available
    try {
      const stored = localStorage.getItem('oob-session');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          sessionExpiresAt: new Date(parsed.sessionExpiresAt),
        };
      }
    } catch {
      // Ignore storage errors
    }
    return null;
  });

  const clearSession = useCallback(() => {
    setSession(null);
    localStorage.removeItem('oob-session');
  }, []);

  const updateSession = useCallback((newSession: OobSession | null) => {
    setSession(newSession);
    if (newSession) {
      localStorage.setItem('oob-session', JSON.stringify(newSession));
    } else {
      localStorage.removeItem('oob-session');
    }
  }, []);

  const isSessionActive = useCallback(() => {
    if (!session) return false;
    return new Date() < session.sessionExpiresAt;
  }, [session]);

  return (
    <OobSessionContext.Provider value={{ session, setSession: updateSession, clearSession, isSessionActive }}>
      {children}
    </OobSessionContext.Provider>
  );
};

export const useOobSession = () => {
  const context = useContext(OobSessionContext);
  if (!context) {
    throw new Error('useOobSession must be used within OobSessionProvider');
  }
  return context;
};
